import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import useSaveAdditionalNotes from '@/hooks/useSaveAdditionalNotes'
import {
  cn,
  hasIncompleteMpsTreatment,
  isFutureIssueDate,
  isMpsMedRequest,
  isMultiProductOrderMedRequest
} from '@/lib/utils'
import { isRoleLimitedToEditingNotes } from '@/lib/permissions'
import type { AdditionalNotesType } from '@/types/data'
import { FooterActions, useFooterActionsState } from './footer-actions'
import MultiProductOrderFooter from './multi-product-order/footer'

const NOTE_ENTRIES: (keyof AdditionalNotesType)[] = ['pharmacist', 'customerService', 'afterCare', 'clinician']

export const areNotesDirty = (notes: AdditionalNotesType, persisted?: AdditionalNotesType | null) =>
  NOTE_ENTRIES.some(entry => (notes[entry] ?? '') !== (persisted?.[entry] ?? ''))

function SaveNotesFooter() {
  const notes = useMedicationRequestStore(state => state.notes)
  const medRequest = useCurrentMedication()
  const data = medRequest?.data
  const { saveAdditionalNotes, isPending } = useSaveAdditionalNotes({ doNotResetStore: true })

  if (!data) return null

  const isDirty = areNotesDirty(notes, data.additionalNotes)
  const isDisabled = !isDirty || isPending || !!data.locked

  return (
    <div className='fixed left-0 bottom-0 flex w-full h-[104px] flex-col justify-center items-center gap-3 shrink-0 border-t border-[#FAFAFB] bg-white shadow-[0px_-8px_80px_0px_rgba(0,0,0,0.10)] z-50'>
      <div className='flex justify-center gap-4 w-full max-w-[994px]'>
        <button
          type='button'
          data-testid='save-notes'
          onClick={saveAdditionalNotes}
          disabled={isDisabled}
          className={cn(
            'flex w-[200px] h-14 flex-col justify-center items-center gap-2 rounded-2xl bg-[#153CB7] py-4 px-6 hover:bg-[#0F2E8A] transition-colors duration-200',
            { 'opacity-50 cursor-not-allowed': isDisabled }
          )}
        >
          <span className='text-white text-center text-sm font-semibold leading-[170%]'>
            {isPending ? 'Saving…' : 'Save notes'}
          </span>
        </button>
      </div>
    </div>
  )
}

export default function Footer() {
  const medRequest = useCurrentMedication()

  if (isRoleLimitedToEditingNotes()) return <SaveNotesFooter />
  if (isMultiProductOrderMedRequest(medRequest?.data)) return <MultiProductOrderFooter />
  return <ActionFooter />
}

function ActionFooter() {
  const { medicationIssueDate, medicationDirection, medication, extraTreatments } = useMedicationRequestStore()
  const state = useFooterActionsState()
  const { data } = state

  if (!data) return null

  const missingDirections = !medicationDirection.trim()

  // a request with an unresolvable ordered variation now opens with nothing selected, so approval
  // waits for a real choice — kept out of the clause below, whose priority bypass would skip it
  const isMedicationMissing = !medication

  const hasIncompleteTreatment = isMpsMedRequest(data) && hasIncompleteMpsTreatment(extraTreatments)

  // OTC skips the approval reason and the scroll.
  const isOtc = medication?.type === 'OTC'

  const isApprovalDisabled =
    state.isDecisionBlocked ||
    isMedicationMissing ||
    missingDirections ||
    (!state.isScrolledDown && !data.isPriority && !isOtc) ||
    hasIncompleteTreatment

  return (
    <FooterActions
      state={state}
      approveLabel={isFutureIssueDate(medicationIssueDate) ? 'Approve for future' : 'Approve Now'}
      isApprovalDisabled={isApprovalDisabled}
      skipsApprovalReason={isOtc}
      rejectLabel='Reject'
      isFollowUpDisabled={state.isFollowUpOrRejectBlocked}
      isRejectDisabled={state.isFollowUpOrRejectBlocked}
    />
  )
}
